// PaymentProcessForm.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { submit, reduxForm, formValueSelector } from 'redux-form';
import { graphql, compose } from 'react-apollo';
import { get, omit } from 'lodash';
import { withStyles } from 'material-ui/styles';
import Stepper, { Step, StepContent, StepLabel } from 'material-ui/Stepper';

import FormHeader from './FormHeader';
import NextButton from './NextButton';
import InvestmentDetails from './InvestmentDetails';
// import SubscriptionAgreement from './SubscriptionAgreement'; // temporarily disabled
import HellosignAgreement from './HellosignAgreement';
import BrokerageAgreement from './BrokerageAgreement';
import SuitabilityQuestion from './SutabilityQuestion';
import CountryOfResidence from './CountryOfResidence';
import InvestmentType from './InvestmentType';
import SutabilityQuestionRegCf from './SutabilityQuestionRegCf';
import InvestmentReview from './InvestmentReview';
import PaymentDetails from './PaymentDetails';
import PaymentVerification from './PaymentVerification';
import PaymentSubmission from './PaymentSubmission';
import { currentInvestmentQuery } from '../../../../../apollo';
import PaymentMethod from './PaymentMethod';

const styles = (theme) => ({
  root: {
    width: '100%',
    padding: 0,
    backgroundColor: '#fff',
  },
  stepRoot: {
    borderBottom: '1px solid #CCCCCC',
    backgroundColor: '#fff',
  },
  active: {
    fontSize: '20px',
    color: theme.palette.secondary.main,
    padding: '10px 0px',
  },
  completed: {
    fontWeight: 300,
    color: '#6E6E6E',
    background: '#F1F1F1',
    cursor: 'pointer',
  },
  disabled: {
    fontWeight: 300,
    fontSize: '16px',
    color: '#6E6E6E',
    background: '#F1F1F1',
    padding: '10px 0px',
  },
  iconContainer: {
    marginLeft: '40px',
  },
  content: {
    border: 'none',
    margin: 0,
    paddingTop: '0px',
    paddingLeft: '40px',
    paddingRight: '40px',
    backgroundColor: '#fff',
  },
});

class PaymentProcessForm extends React.Component {
  getBaseSteps = () => [
    { component: InvestmentDetails, title: 'Investment Details' },
    { component: CountryOfResidence, title: 'Country of Residence' },
    { component: InvestmentType, title: 'Type of Investment' },
    { component: SuitabilityQuestion, title: 'Suitability Questions' },
    { component: SutabilityQuestionRegCf, title: 'suitability Questions', type: 'REGCF' },
    { component: HellosignAgreement, title: 'Subscription Agreement' },
    { component: BrokerageAgreement, title: 'Brokerage Agreement' },
    { component: PaymentMethod, title: 'Payment Method' },
    { component: PaymentDetails, title: 'Payment Details' },
    { component: PaymentVerification, title: 'Payment Verification' },
    { component: InvestmentReview, title: 'Investment Review' },
    { component: PaymentSubmission, title: 'Payment Submission' },
  ];

  buildSteps = () => {
    const { offer, selectedPaymentMethod, selectedPaymentType } = this.props;

    let steps = this.getBaseSteps();

    // Only show "Payment Method" when BANK + not offline (matches your earlier intention)
    const shouldShowPaymentMethod =
      selectedPaymentMethod === 'BANK' && selectedPaymentType !== 'BANK_OFFLINE';

    if (!shouldShowPaymentMethod) {
      steps = steps.filter((s) => s.title !== 'Payment Method');
    }

    // bypassDocusign removes Subscription Agreement
    if (offer.bypassDocusign) {
      steps = steps.filter((s) => s.title !== 'Subscription Agreement');
    }

    // Offer-type specific step removals (preserves original logic)
    if (
      offer.typeOfOffer === 'REG_D' ||
      offer.typeOfOffer === 'Reg_D_506b' ||
      offer.typeOfOffer === 'Reg_D_506c'
    ) {
      steps = steps.filter(
        (s) =>
          s.title !== 'Investment Review' &&
          s.title !== 'Brokerage Agreement' &&
          s.type !== 'REGCF'
      );
    } else if (offer.typeOfOffer === 'Carbon_Credit' || !offer.typeOfOffer) {
      steps = steps.filter(
        (s) =>
          s.title !== 'Investment Review' &&
          s.title !== 'Brokerage Agreement' &&
          s.title !== 'Country of Residence' &&
          s.type !== 'REGCF'
      );
    } else if (offer.typeOfOffer === 'REG_CF') {
      steps = steps.filter(
        (s) =>
          s.title !== 'Suitability Questions' &&
          s.title !== 'Brokerage Agreement'
      );
    } else if (offer.typeOfOffer === 'REG_A') {
      steps = steps.filter((s) => s.type !== 'REGCF' && s.title !== 'Investment Review');
    }

    return steps;
  };

  getNextStepTitle = (steps) => {
    const { activeStep } = this.props;
    const nextTitle = get(steps[activeStep + 1], 'title');
    return nextTitle ? `NEXT: ${nextTitle.toUpperCase()}` : 'DONE';
  };

  render() {
    const {
      classes,
      investorUser,
      tenant,
      offer,
      handleSubmit,
      activeStep,
      handleStep,
      handleClose,
      toggleCancelDialog,
      dispatch,
      change,
      initialValues,
      investmentStatus,
      selectedPaymentMethod,
      selectedPaymentType,
      paymentDetails,
      income,
      networth,
    } = this.props;

    const steps = this.buildSteps();

    const { paymentSent } = initialValues;
    const disabled = paymentSent;

    // Your request: always enable after Subscription Agreement -> no gating here
    const blockNext = false;

    // Critical: stop StepLabel clicks from behaving like form submit / dialog close
    // Also block direct clicking into Payment Submission to avoid “auto-close” paths.
    const nonClickableTitles = new Set(['Payment Submission']);

    const guardedHandleStep = (index, title) => (evt) => {
      if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
      if (evt && typeof evt.stopPropagation === 'function') evt.stopPropagation();

      // Don't allow direct navigation into final step via label click.
      if (nonClickableTitles.has(title)) return;

      return handleStep(index)();
    };

    return (
      <div className={classes.root}>
        <FormHeader
          disabled={disabled}
          name={offer.name}
          investmentStatus={investmentStatus}
          handleClose={handleClose}
          toggleCancelDialog={toggleCancelDialog}
        />

        <Stepper
          connector={<span />}
          activeStep={activeStep}
          orientation="vertical"
          classes={{ root: classes.root }}
        >
          {steps.map(({ component: Component, title }, index) => (
            <Step key={title} classes={{ root: classes.stepRoot }}>
              <StepLabel
                onClick={guardedHandleStep(index, title)}
                classes={{
                  root: classes.active,
                  active: classes.active,
                  completed: classes.completed,
                  disabled: classes.completed,
                  iconContainer: classes.iconContainer,
                }}
              >
                {title}
              </StepLabel>

              <StepContent classes={{ root: classes.content }}>
                <Component
                  disabled={disabled}
                  offer={offer}
                  initialValues={initialValues}
                  investorUser={investorUser}
                  tenant={tenant}
                  handleSubmit={handleSubmit}
                  selectedPaymentMethod={selectedPaymentMethod}
                  selectedPaymentType={selectedPaymentType}
                  paymentDetails={paymentDetails}
                  dispatch={dispatch}
                  change={change}
                  income={income}
                  networth={networth}
                  handleClose={handleClose}
                  totalSteps={steps.length - 1}
                />
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <NextButton
          onClick={() => {
            dispatch(submit('paymentProcessForm'));
          }}
          nextStepTitle={this.getNextStepTitle(steps)}
          activeStep={activeStep}
          disabled={blockNext}
        />
      </div>
    );
  }
}

PaymentProcessForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  offer: PropTypes.object.isRequired,
  investorUser: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  activeStep: PropTypes.number.isRequired,
  tenant: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleStep: PropTypes.func.isRequired,
  toggleCancelDialog: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  paymentDetails: PropTypes.object.isRequired,
  investmentStatus: PropTypes.string.isRequired,
  selectedPaymentMethod: PropTypes.string.isRequired,
  selectedPaymentType: PropTypes.string.isRequired,

  // still mapped for compatibility
  hellosignSignatureStatus: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const defaultPaymentMethod = ownProps.offer.paymentDetails.acceptedPaymentMethod[0];
  const defaultPaymentDetails = ownProps.offer.paymentDetails;

  const currentInvestment = get(ownProps.data, 'currentInvestment', {
    investmentStatus: 'NOT_STARTED',
    paymentMethod: defaultPaymentMethod,
    paymentType: null,
    income: state.investorUser.income,
    networth: state.investorUser.networth,
  });

  const normalizedInvestment = omit(currentInvestment, ['__typename', 'paymentDetails']);

  const selector = formValueSelector('paymentProcessForm');

  return {
    investorUser: state.investorUser,
    tenant: state.tenant,
    selectedPaymentMethod: selector(state, 'paymentMethod') || defaultPaymentMethod,
    selectedPaymentType: selector(state, 'paymentType') || '',
    investmentStatus: selector(state, 'investmentStatus') || 'NOT_STARTED',
    paymentDetails: get(ownProps.data, 'currentInvestment.paymentDetails', defaultPaymentDetails),
    income: selector(state, 'income') || '',
    networth: selector(state, 'networth') || '',

    // kept for compatibility, not used to gate
    hellosignSignatureStatus: selector(state, 'hellosign_signature_status') || 'none',

    initialValues: {
      ...normalizedInvestment,
    },
  };
};

const withData = (ComponentToWrap) => (otherProps) => {
  const { update, validate } = otherProps;

  const WithData = compose(
    graphql(currentInvestmentQuery, {
      skip: (props) => !props.investmentId,
      options: (props) => ({
        variables: { investmentId: props.investmentId },
        fetchPolicy: 'network-only',
      }),
    }),
    connect(mapStateToProps),
    reduxForm({
      validate,
      form: 'paymentProcessForm',
      onSubmit: (values, dispatch, props) => {
        const cleaned = omit(values, ['hellosign_signature_status']);
        return update(cleaned, dispatch, props);
      },
      enableReinitialize: true,
      destroyOnUnmount: false,
      forceUnregisterOnUnmount: true,
    })
  )(ComponentToWrap);

  WithData.propTypes = {
    investmentId: PropTypes.string,
  };

  WithData.defaultProps = {
    investmentId: null,
  };

  return <WithData {...otherProps} />;
};

export default withStyles(styles)(withData(PaymentProcessForm));



// WEBPACK FOOTER //
// ./src/components/OfferOverview/ReadyToInvestButton/PaymentProcess/PaymentProcessForm/index.js